import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, Sun, Moon, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage, useT } from './i18n';
import { useTheme } from './ThemeProvider';
type NavItem = {
  name: string;
  path: string;
};
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileGroup, setMobileGroup] = useState<string | null>(null);
  const location = useLocation();
  const {
    lang,
    setLang
  } = useLanguage();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const t = useT();
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
    setOpenMenu(null);
    setMobileGroup(null);
  }, [location.pathname]);
  // Close desktop dropdown on outside click / Escape
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);
  const solucoes: NavItem[] = [{
    name: t.nav.paraVoce,
    path: '/para-voce'
  }, {
    name: t.nav.paraNegocio,
    path: '/para-negocio'
  }, {
    name: t.nav.paraMarca,
    path: '/para-marca'
  }, {
    name: t.nav.paraOng,
    path: '/ongs'
  }];
  const ecossistema: NavItem[] = [{
    name: t.nav.educa,
    path: '/educa'
  }, {
    name: t.nav.gov,
    path: '/gov'
  }, {
    name: t.nav.clube,
    path: '/clube'
  }, {
    name: t.nav.tropa,
    path: '/tropa'
  }];
  const singles: NavItem[] = [{
    name: t.nav.inicio,
    path: '/'
  }, {
    name: t.nav.ods,
    path: '/ods'
  }, {
    name: t.nav.contato,
    path: '/contato'
  }];
  const isActive = (path: string) => location.pathname === path;
  const groupActive = (items: NavItem[]) => items.some(i => location.pathname === i.path);
  const LANGS: {
    code: typeof lang;
    label: string;
    name: string;
  }[] = [{
    code: 'pt',
    label: 'PT',
    name: 'Português'
  }, {
    code: 'en',
    label: 'EN',
    name: 'English'
  }, {
    code: 'es',
    label: 'ES',
    name: 'Español'
  }, {
    code: 'ja',
    label: 'JA',
    name: '日本語'
  }, {
    code: 'zh',
    label: 'ZH',
    name: '中文'
  }];
  const LanguageToggle = () => {
    const open = openMenu === 'lang';
    const current = LANGS.find(l => l.code === lang) ?? LANGS[0];
    return <div className="relative" data-id="element-74" data-mp="oo9jjsrcpum">
        <button type="button" aria-haspopup="true" aria-expanded={open} aria-label="Selecionar idioma / Select language" onClick={() => setOpenMenu(open ? null : 'lang')} className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-brand-charcoal dark:text-gray-200 px-3 py-1.5 rounded-full text-xs font-bold transition-colors focus-visible:ring-2 focus-visible:ring-brand-green-dark dark:focus-visible:ring-brand-green-light" data-id="element-75" data-mp="pze3lu121ms">
          <Globe size={14} aria-hidden="true" data-id="element-76" data-mp="345wq80br1jnr" />
          <span data-id="element-77" data-mp="346pmg7d30qrn">{current.label}</span>
          <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-78" data-mp="343jzaxo0jyyk" />
        </button>
        <AnimatePresence data-id="element-79" data-mp="3enjt6dg7q7m5">
          {open && <motion.ul initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.18,
          ease: 'easeOut'
        }} className="absolute right-0 top-full mt-3 w-44 bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-2 z-50" data-id="element-80" data-mp="3amq4viyuyqsq">
              {LANGS.map(l => <li key={l.code} data-id="element-81" data-mp="2hzlroqpm365v">
                  <button type="button" onClick={() => {
              setLang(l.code);
              setOpenMenu(null);
            }} aria-current={l.code === lang ? 'true' : undefined} className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${l.code === lang ? 'bg-brand-green/10 text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/80 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-82" data-mp="2hwhh9bljze6r">
                    <span data-id="element-83" data-mp="2lo4si2nnhpb7">{l.name}</span>
                    <span className="text-xs opacity-60" data-id="element-84" data-mp="2qklqrpokdk20">{l.label}</span>
                  </button>
                </li>)}
            </motion.ul>}
        </AnimatePresence>
      </div>;
  };
  const ThemeToggle = () => <button onClick={toggleTheme} aria-label={theme === 'light' ? t.common.darkModeOn : t.common.darkModeOff} aria-pressed={theme === 'dark'} className="p-2 text-brand-charcoal dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-brand-green-dark dark:focus-visible:ring-brand-green-light" data-id="element-85" data-mp="30ijgyipzhqx9">
      {theme === 'light' ? <Moon size={20} aria-hidden="true" data-id="element-86" data-mp="182i4ikwkaa28" /> : <Sun size={20} aria-hidden="true" data-id="element-87" data-mp="18433cmpwsf5w" />}
    </button>;
  const linkBase = 'text-sm font-bold transition-colors hover:text-brand-green-dark dark:hover:text-brand-green-light';
  const Dropdown = ({
    id,
    label,
    items
  }: {
    id: string;
    label: string;
    items: NavItem[];
  }) => {
    const open = openMenu === id;
    const active = groupActive(items);
    return <li className="relative" onMouseEnter={() => setOpenMenu(id)} onMouseLeave={() => setOpenMenu(cur => cur === id ? null : cur)} data-id="element-88" data-mp="1qshq2xt7170w">
        <button type="button" aria-haspopup="true" aria-expanded={open} onClick={() => setOpenMenu(open ? null : id)} className={`flex items-center gap-1 ${linkBase} ${active || open ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/70 dark:text-gray-300'}`} data-id="element-89" data-mp="1zuzeniwbmbr2">
          {label}
          <ChevronDown size={15} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-90" data-mp="25g8d43lndz5w" />
        </button>
        <AnimatePresence data-id="element-91" data-mp="2agw2yly549yj">
          {open && <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 8
        }} transition={{
          duration: 0.18,
          ease: 'easeOut'
        }} className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-60" data-id="element-92" data-mp="1vp3y53vz2eb2">
              <ul className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-2" data-id="element-93" data-mp="1hpceonmos5po">
                {items.map(item => <li key={item.path} data-id="element-94" data-mp="1s9kbb2hao3aq">
                    <Link to={item.path} aria-current={isActive(item.path) ? 'page' : undefined} className={`block px-4 py-3 rounded-xl text-sm font-bold transition-colors ${isActive(item.path) ? 'bg-brand-green/10 text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/80 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`} data-id="element-95" data-mp="1saluju8grw3j">
                      {item.name}
                    </Link>
                  </li>)}
              </ul>
            </motion.div>}
        </AnimatePresence>
      </li>;
  };
  const MobileGroup = ({
    id,
    label,
    items
  }: {
    id: string;
    label: string;
    items: NavItem[];
  }) => {
    const open = mobileGroup === id;
    return <li data-id="element-96" data-mp="qkqj63vcrc6z">
        <button type="button" aria-expanded={open} onClick={() => setMobileGroup(open ? null : id)} className={`w-full flex items-center justify-between font-extrabold tracking-tight ${groupActive(items) ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal dark:text-white'}`} data-id="element-97" data-mp="qm1m61gz4fx1">
          {label}
          <ChevronDown size={22} className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-98" data-mp="3g6jos2gx6q9f" />
        </button>
        <AnimatePresence data-id="element-99" data-mp="2ogyrdyxo3ht3">
          {open && <motion.ul initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: 'easeOut'
        }} className="overflow-hidden pl-4 mt-4 space-y-4 border-l-2 border-brand-green/30" data-id="element-100" data-mp="2odjacqkfnyp1">
              {items.map(item => <li key={item.path} data-id="element-101" data-mp="a9roxn4v3n6k">
                  <Link to={item.path} aria-current={isActive(item.path) ? 'page' : undefined} className={`block text-lg font-bold ${isActive(item.path) ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/80 dark:text-gray-300'}`} data-id="element-102" data-mp="ecwnxfpqh6qg">
                    {item.name}
                  </Link>
                </li>)}
            </motion.ul>}
        </AnimatePresence>
      </li>;
  };
  return <nav ref={navRef} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-sm py-4 border-b border-gray-100 dark:border-gray-800' : 'bg-transparent py-6'}`} data-id="element-103" data-mp="dopip1ea2nb">
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-6" data-id="element-104" data-mp="33zvph0mfyq3a">
        <Link to="/" className="flex items-center gap-2 z-50 group shrink-0" data-id="element-105" data-mp="2whxstwpcz9hh">
          <span className="font-sans font-extrabold text-2xl text-brand-charcoal dark:text-white tracking-tight group-hover:text-brand-green transition-colors" data-id="element-106" data-mp="2rkxlligrw0os">
            Bairrou<span className="text-brand-green" data-id="element-107" data-mp="2ijhh1rcn2ssh">App</span>
            <span className="text-brand-yellow font-display text-3xl leading-none" data-id="element-108" data-mp="2il0u79300nq1">
              !
            </span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-7" data-id="element-109" data-mp="28rk34cu8xmq2">
          <ul className="flex items-center gap-7" data-id="element-110" data-mp="23tifdelvkz89">
            <li data-id="element-111" data-mp="23rz11tjs4y94">
              <Link to="/" aria-current={isActive('/') ? 'page' : undefined} className={`${linkBase} ${isActive('/') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/70 dark:text-gray-300'}`} data-id="element-112" data-mp="1yuyuzimxjvi0">
                {t.nav.inicio}
              </Link>
            </li>
            <Dropdown id="solucoes" label={t.nav.solucoes} items={solucoes} data-id="element-113" data-mp="1g5vez3cheuni" />
            <Dropdown id="ecossistema" label={t.nav.ecossistema} items={ecossistema} data-id="element-114" data-mp="1k8iabe8d2xpg" />
            <li data-id="element-115" data-mp="16abj2gxx3vyr">
              <Link to="/ods" aria-current={isActive('/ods') ? 'page' : undefined} className={`${linkBase} ${isActive('/ods') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/70 dark:text-gray-300'}`} data-id="element-116" data-mp="16atoo46gzmjc">
                {t.nav.ods}
              </Link>
            </li>
            <li data-id="element-117" data-mp="1p6jslwxyqszv">
              <Link to="/investidores" aria-current={isActive('/investidores') ? 'page' : undefined} className={`${linkBase} ${isActive('/investidores') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/70 dark:text-gray-300'}`} data-id="element-118" data-mp="1p72zmim2rwz4">
                {t.nav.paraInvestidor}
              </Link>
            </li>
            <li data-id="element-119" data-mp="b66uj67wa4vt">
              <Link to="/contato" aria-current={isActive('/contato') ? 'page' : undefined} className={`${linkBase} ${isActive('/contato') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal/70 dark:text-gray-300'}`} data-id="element-120" data-mp="b5nddrq2jkiu">
                {t.nav.contato}
              </Link>
            </li>
          </ul>
          <div className="flex items-center gap-4 pl-6 border-l border-gray-200 dark:border-gray-700" data-id="element-121" data-mp="3oejqyoodynku">
            <ThemeToggle data-id="element-122" data-mp="3jh9u4nyo39mz" />
            <LanguageToggle data-id="element-123" data-mp="19kagjne7huu" />
            <span className="bg-brand-green/10 text-brand-green-dark dark:text-brand-green-light px-4 py-2 rounded-full text-xs font-bold tracking-wide uppercase cursor-default select-none whitespace-nowrap" data-id="element-124" data-mp="1an3oy86twdx">
              {t.common.disponivelEmBreve}
            </span>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex items-center gap-3 lg:hidden z-50" data-id="element-125" data-mp="17pecrp7gsnj1">
          <ThemeToggle data-id="element-126" data-mp="230vztxed7xe5" />
          <LanguageToggle data-id="element-127" data-mp="231duwqpak5kh" />
          <button className="p-2 text-brand-charcoal dark:text-gray-300" onClick={() => setIsOpen(!isOpen)} aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'} aria-expanded={isOpen} aria-controls="mobile-menu" data-id="element-128" data-mp="2326yxjwv8gyx">
            {isOpen ? <X size={24} aria-hidden="true" data-id="element-129" data-mp="2lyot07z6ssyc" /> : <Menu size={24} aria-hidden="true" data-id="element-130" data-mp="2lyeziaexo3nw" />}
          </button>
        </div>

        {/* Mobile Nav */}
        <div id="mobile-menu" aria-hidden={!isOpen} {...!isOpen ? {
        inert: ''
      } : {}} className={`fixed inset-0 bg-white dark:bg-gray-900 z-40 flex flex-col pt-28 px-8 overflow-y-auto transition-transform duration-300 lg:hidden ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-131" data-mp="2qvf8at363kip">
          <ul className="flex flex-col gap-6 text-2xl pb-8" data-id="element-132" data-mp="39m6oadhnu962">
            <li data-id="element-133" data-mp="y1pagtkpffss">
              <Link to="/" aria-current={isActive('/') ? 'page' : undefined} className={`block font-extrabold tracking-tight ${isActive('/') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal dark:text-white'}`} data-id="element-134" data-mp="y2h3pxqtx1fw">
                {t.nav.inicio}
              </Link>
            </li>
            <MobileGroup id="solucoes" label={t.nav.solucoes} items={solucoes} data-id="element-135" data-mp="1hnvdsmwtzyp5" />
            <MobileGroup id="ecossistema" label={t.nav.ecossistema} items={ecossistema} data-id="element-136" data-mp="1cqlpxbp3bv3f" />
            <li data-id="element-137" data-mp="130ipwrjjxgvl">
              <Link to="/ods" aria-current={isActive('/ods') ? 'page' : undefined} className={`block font-extrabold tracking-tight ${isActive('/ods') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal dark:text-white'}`} data-id="element-138" data-mp="12yz8gxm5szs8">
                {t.nav.ods}
              </Link>
            </li>
            <li data-id="element-139" data-mp="3txvjopyn6z0h">
              <Link to="/investidores" aria-current={isActive('/investidores') ? 'page' : undefined} className={`block font-extrabold tracking-tight ${isActive('/investidores') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal dark:text-white'}`} data-id="element-140" data-mp="3tyddlfxu1158">
                {t.nav.paraInvestidor}
              </Link>
            </li>
            <li data-id="element-141" data-mp="3b8f6b21uzc2n">
              <Link to="/contato" aria-current={isActive('/contato') ? 'page' : undefined} className={`block font-extrabold tracking-tight ${isActive('/contato') ? 'text-brand-green-dark dark:text-brand-green-light' : 'text-brand-charcoal dark:text-white'}`} data-id="element-142" data-mp="3b8xb4n2l6ym6">
                {t.nav.contato}
              </Link>
            </li>
          </ul>
          <div className="mt-auto pb-12" data-id="element-143" data-mp="10et3cmlt73a3">
            <span className="block text-center w-full bg-brand-green/10 text-brand-green-dark dark:text-brand-green-light px-6 py-4 rounded-2xl text-sm font-bold tracking-wide uppercase cursor-default select-none" data-id="element-144" data-mp="1aizs1p2ic76m">
              {t.common.disponivelEmBreve}
            </span>
          </div>
        </div>
      </div>
    </nav>;
}